import AssignmentLabelSelector from '@app/components/AssignmentLabelSelector'
import AssignmentPrioritySelector from '@app/components/AssignmentPrioritySelector'
import AssociatedContactsSelector from '@app/components/AssociatedContactsSelector'
import AssociatedPropertiesSelector from '@app/components/AssociatedPropertiesSelector'
import CheckBox from '@app/components/CheckBox'
import CompanySelector from '@app/components/CompanySelector'
import DateSelector from '@app/components/DateSelector'
import { FileDropzone, FileProgress } from '@app/components/FileUpload'
import FormGroupRenderer, {
  ILabelColumnSize,
} from '@app/components/FormGroupRenderer'
import LotSizeInput from '@app/components/LotSizeInput'
import MentionEditor from '@app/components/MentionEditor'
import PillListRenderer from '@app/components/PillListRenderer'
import RemindersEditor from '@app/components/RemindersEditor'
import { AssignmentSourceType } from '@app/models/enums'
import * as React from 'react'
import { Col, Input, Row } from 'reactstrap'
import * as AssignmentFormStore from '../modules/assignmentForm'

interface IComponentProps {
  id?: string
}

type AssignmentFormProps = IComponentProps &
  AssignmentFormStore.IAssignmentFormState &
  typeof AssignmentFormStore.actionCreators

export default class AssignmentForm extends React.PureComponent<
  AssignmentFormProps
> {
  constructor(props: AssignmentFormProps) {
    super(props)

    if (this.props.id) {
      this.props.loadAssignment(this.props.id)
    }
  }

  public componentWillUnmount() {
    this.props.resetStore()
  }

  public render() {
    const dropzoneStyle: React.CSSProperties = {
      borderColor: 'rgb(102, 102, 102)',
      borderRadius: '5px',
      borderStyle: 'dashed',
      borderWidth: '1px',
      display: 'table',
      height: '100px',
      width: '100%',
    }

    const dropzoneActiveStyle: React.CSSProperties = {
      borderColor: '#9aa41a',
      borderWidth: '2px',
    }

    const labelColSizes: ILabelColumnSize = {
      xs: 12,
    }

    const isFromBrokeragePipeline =
      this.props.assignment.source !== undefined &&
      this.props.assignment.source.type ===
        AssignmentSourceType.BrokeragePipeline

    return (
      <div>
        <div className='mb-2'>
          <Input
            onChange={(e) => this.props.setTitleText(e.target.value)}
            placeholder='Title'
            required
            type='text'
            value={this.props.assignment.title}
          />
        </div>
        {isFromBrokeragePipeline && (
          <FormGroupRenderer labelColSizes={labelColSizes} labelText='Pipeline'>
            {this.props.assignment.source!.name}
          </FormGroupRenderer>
        )}
        <Row>
          <Col lg={6}>
            <FormGroupRenderer
              labelColSizes={labelColSizes}
              labelText='Priority'
            >
              <AssignmentPrioritySelector
                onChange={this.props.setPriority}
                value={this.props.assignment.priority}
              />
            </FormGroupRenderer>
          </Col>
          <Col lg={6}>
            <FormGroupRenderer
              labelColSizes={labelColSizes}
              labelText='Due Date'
            >
              <DateSelector
                onChange={this.props.setDueDate}
                value={this.props.assignment.dueDate}
              />
            </FormGroupRenderer>
          </Col>
        </Row>
        <Row>
          <Col lg={6}>
            <FormGroupRenderer
              labelColSizes={labelColSizes}
              labelText='Company'
            >
              <CompanySelector
                onChange={this.props.setCompany}
                value={this.props.assignment.company}
              />
            </FormGroupRenderer>
          </Col>
          <Col lg={6}>
            <FormGroupRenderer labelColSizes={labelColSizes} labelText='Label'>
              <AssignmentLabelSelector
                onChange={this.props.setLabel}
                value={this.props.assignment.label}
              />
            </FormGroupRenderer>
          </Col>
        </Row>
        <Row>
          <Col lg={6}>
            <FormGroupRenderer
              labelColSizes={labelColSizes}
              labelText='Connections'
            >
              <AssociatedContactsSelector
                onChange={this.props.setContacts}
                value={this.props.assignment.contacts}
              />
            </FormGroupRenderer>
          </Col>
          <Col lg={6}>
            <FormGroupRenderer
              labelColSizes={labelColSizes}
              labelText='$ Opportunity'
              onChange={this.props.setOpportunityDollar}
              placeholder='Enter a number'
              step='0.01'
              type='number'
              value={this.props.assignment.dollarOpportunity}
            />
          </Col>
        </Row>
        <Row>
          <Col lg={6}>
            <FormGroupRenderer
              labelColSizes={labelColSizes}
              labelText='Properties'
            >
              {!isFromBrokeragePipeline && (
                <AssociatedPropertiesSelector
                  onChange={this.props.setProperty}
                  value={this.props.assignment.properties}
                />
              )}
              {isFromBrokeragePipeline && (
                <PillListRenderer
                  list={this.props.assignment.properties}
                  idValue='propertyId'
                  noLink
                />
              )}
            </FormGroupRenderer>
          </Col>
          <Col lg={6}>
            <FormGroupRenderer labelColSizes={labelColSizes} labelText='Size'>
              <LotSizeInput
                onChange={this.props.setLotSize}
                value={this.props.assignment.lotSize}
              />
            </FormGroupRenderer>
          </Col>
        </Row>
        <div className='mb-2'>
          <CheckBox
            onChange={this.props.setCompleted}
            value={this.props.assignment.isComplete}
          >
            Complete
          </CheckBox>
        </div>
        <FormGroupRenderer labelColSizes={labelColSizes} labelText='Notes'>
          <MentionEditor
            compact
            classNames='form-control'
            id='assignment-form-new'
            editorState={this.props.assignment.markupState}
            onChange={this.props.setMarkup}
            placeholder='Enter a note...'
          />
        </FormGroupRenderer>
        <FormGroupRenderer
          labelColSizes={labelColSizes}
          labelText='Assigned To'
        >
          <AssociatedContactsSelector
            onChange={this.props.setAssignedTo}
            value={this.props.assignment.assignedTo}
          />
        </FormGroupRenderer>
        <FormGroupRenderer
          labelColSizes={labelColSizes}
          labelText='Attachments'
        >
          <FileDropzone
            style={dropzoneStyle}
            identifier='assignment-form'
            activeStyle={dropzoneActiveStyle}
          >
            <p
              style={{
                display: 'table-cell',
                textAlign: 'center',
                verticalAlign: 'middle',
              }}
            >
              Drag and drop or click here to select files
            </p>
          </FileDropzone>
          <FileProgress
            identifier='assignment-form'
            dataSource={this.props.assignment.attachments}
          />
        </FormGroupRenderer>
        <FormGroupRenderer labelColSizes={labelColSizes} labelText='reminders'>
          <RemindersEditor
            onChange={this.props.setReminders}
            value={this.props.assignment.reminders}
          />
        </FormGroupRenderer>
      </div>
    )
  }
}
